/**
 * SEO Configuration
 * Centralized configuration for all SEO-related settings
 */

import config from 'config';

// Site configuration. Brand is baked into config at build time
// (config.profitroll.json / config.jobalto.json); each brand has its own build.
const isJobalto = config.brand === 'jobalto';

// This brand's canonical root. Derived from the brand (not config.rootUrl,
// which is overloaded elsewhere as the profitroll base — see variables/main.js)
// so jobalto's own url/logo/JSON-LD stop pointing at profitroll.com.ua.
const brandRootUrl = isJobalto ? 'https://jobalto.com' : 'https://profitroll.com.ua';

export const SITE_CONFIG = {
  name: isJobalto ? 'Jobalto' : 'Профітроль',
  alternateName: 'Profitroll',
  description: 'Career planning and promising occupations platform',
  url: brandRootUrl,
  jobaltoUrl: 'https://jobalto.com',
  // Brand logo that actually exists in public/images (the old /logo.png 404s
  // to the SPA shell). SVG — dimensions in the ImageObject are nominal.
  logo: `${brandRootUrl}/images/${isJobalto ? 'jobatlas-logo.svg' : 'profitroll-logo.svg'}`,
  email: 'support@jobalto.com',
  telephone: '+380448005500',
  foundingDate: '2024',
  socialProfiles: [
    'https://www.tiktok.com/@prftroll',
    'https://www.instagram.com/prftroll',
    'https://www.linkedin.com/company/jobatlas.llc'
  ],
  // The bundled preview lives at /images/preview.png; /assets/images/... 404s
  // to the SPA shell, breaking every og:image/twitter:image.
  defaultImage: '/images/preview.png',
  twitterHandle: isJobalto ? '@jobalto' : '@prftroll'
};

// Language to country mapping for hreflang
// Only languages that have occupation translations in the database (23 languages)
// Removed: bg, ca, el, hu, hy, ka, mk, sk, sl (no occupation translations)
export const LANG_TO_COUNTRY = {
  ua: 'UA',
  cs: 'CZ',
  da: 'DK',
  de: 'DE',
  en: 'GB',
  es: 'ES',
  et: 'EE',
  fi: 'FI',
  fr: 'FR',
  hr: 'HR',
  is: 'IS',
  it: 'IT',
  lt: 'LT',
  lv: 'LV',
  nl: 'NL',
  no: 'NO',
  pl: 'PL',
  pt: 'PT',
  ro: 'RO',
  sq: 'AL',
  sr: 'RS',
  sv: 'SE',
  tr: 'TR'
};

// Page types for structured data
export const PAGE_TYPES = {
  HOME: 'home',
  PROFESSION: 'profession',
  PROFESSIONS: 'professions',
  ARTICLE: 'article',
  ARTICLES: 'articles',
  SKILL: 'skill',
  SKILLS: 'skills',
  VACANCY: 'vacancy',
  VACANCIES: 'vacancies',
  COURSE: 'course',
  COURSES: 'courses',
  CONSULTANT: 'consultant',
  CONSULTANTS: 'consultants',
  MENTOR: 'mentor',
  MENTORS: 'mentors',
  EDUCATION_PROGRAM: 'educationProgram',
  EDUCATION_PROGRAMS: 'educationPrograms',
  PLATFORM: 'platform',
  PLATFORMS: 'platforms',
  COMPANY: 'company',
  ABOUT: 'about',
  POLICY: 'policy'
};

// Default priorities for sitemap
export const SITEMAP_PRIORITIES = {
  home: 1.0,
  professions: 0.9,
  profession: 1.0,
  articles: 0.8,
  article: 0.7,
  vacancies: 0.9,
  vacancy: 0.8,
  skills: 0.7,
  skill: 0.6,
  courses: 0.7,
  course: 0.6,
  consultants: 0.8,
  consultant: 0.7,
  mentors: 0.7,
  mentor: 0.6,
  about: 0.5,
  policy: 0.3
};

// Schema.org types mapping
export const SCHEMA_TYPES = {
  profession: 'Occupation',
  article: 'Article',
  vacancy: 'JobPosting',
  course: 'Course',
  consultant: 'Person',
  mentor: 'Person',
  educationProgram: 'EducationalOrganization',
  platform: 'EducationalOrganization',
  company: 'Organization'
};

const seoConfig = {
  SITE_CONFIG,
  LANG_TO_COUNTRY,
  PAGE_TYPES,
  SITEMAP_PRIORITIES,
  SCHEMA_TYPES
};

export default seoConfig;



